<template>
  <div class="nutritionist-dashboard">
    <!-- Header -->
    <div class="dashboard-header">
      <h1 class="dashboard-title">NUTRITION</h1>
      <p class="dashboard-subtitle">Équilibre & Acceptation des menus</p>
    </div>

    <!-- Cartes principales -->
    <div class="cards-grid">
      <!-- Card 1: Acceptation par catégorie (décision E) -->
      <dashboard-card
        title="🍽️ Acceptation"
        description="Appréciation des menus par catégorie sur votre périmètre"
        :badge="acceptanceBadge"
        badge-variant="green"
        link-label="Analyser"
        variant="featured"
        @click="showAcceptance = true"
      />

      <!-- Card 2: Planification menus (périmètre nutritionniste) -->
      <dashboard-card
        title="🗓️ Planification"
        description="Menus à venir sur les cantines de votre périmètre"
        :badge="planningBadge"
        link-label="Voir"
        @click="showMenuPlanning = true"
      />

      <!-- Card 3: Recommandations (accès conservé) -->
      <dashboard-card
        title="📝 Recommandations"
        description="Créer vos recommandations nutritionnelles"
        link-label="Créer"
        @click="goToActionPage"
      />

      <!-- Card 4: Impact réel (accès conservé) -->
      <dashboard-card
        title="📊 Impact réel"
        description="Évaluer l'impact réel de vos actions"
        link-label="Évaluer"
        @click="goToActionPlan"
      />

      <!-- Card 5: Signaux de vigilance (accès conservé) -->
      <dashboard-card
        title="🔎 Signaux de vigilance"
        description="Suivre les signaux de vigilance alimentaire"
        link-label="Suivre"
        @click="goToOnEatingIssuePage"
      />
    </div>

    <!-- Modal: Acceptation -->
    <div v-if="showAcceptance" class="modal-overlay" @click="showAcceptance = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showAcceptance = false">✕</button>
        <h2 class="modal-title">🍽️ Acceptation par catégorie</h2>

        <p v-if="acceptanceLoading" class="feedback-empty">Chargement…</p>
        <p v-else-if="acceptanceError" class="feedback-empty">{{ acceptanceError }}</p>
        <p v-else-if="!acceptance.displayed" class="feedback-empty">
          Aucune cantine de votre périmètre n'a activé le suivi nutritionnel.
        </p>
        <p v-else-if="!acceptance.categories.length" class="feedback-empty">
          Aucune note de menu sur la période. Les notes proviennent des retours des superviseurs de service.
        </p>
        <div v-else class="acceptance-list">
          <div v-for="item in acceptance.categories" :key="item.category" class="acceptance-item">
            <div class="acceptance-category">
              <span class="category-name">{{ item.categoryLabel }}</span>
              <span class="acceptance-meta">
                {{ item.ratingCount }} note{{ item.ratingCount > 1 ? "s" : "" }} · {{ item.canteenCount }} cantine{{
                  item.canteenCount > 1 ? "s" : ""
                }}
              </span>
            </div>
            <span class="acceptance-score" :class="item.averageRating >= 3.5 ? 'text-green' : 'text-orange'">
              {{ formatRating(item.averageRating) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Planification -->
    <div v-if="showMenuPlanning" class="modal-overlay" @click="showMenuPlanning = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showMenuPlanning = false">✕</button>
        <h2 class="modal-title">🗓️ Planification des menus</h2>

        <p v-if="planningLoading" class="feedback-empty">Chargement…</p>
        <p v-else-if="planningError" class="feedback-empty">{{ planningError }}</p>
        <p v-else-if="!menuPlanning.days.length" class="feedback-empty">
          Aucun menu planifié sur les deux prochaines semaines. Les menus proviennent des fichiers importés.
        </p>
        <div v-else class="detail-section">
          <h3 class="section-title">Prochains menus</h3>

          <div v-for="day in menuPlanning.days" :key="day.date" class="planning-card">
            <div class="planning-date">{{ formatFullDate(day.date) }}</div>
            <div v-for="menu in day.menus" :key="menu.canteenId" class="planning-canteen">
              <div v-if="day.menus.length > 1" class="planning-canteen-name">{{ menu.canteenName }}</div>
              <div class="menu-list">
                <div v-for="item in menu.items" :key="item.category" class="menu-item">
                  <span class="menu-label">{{ item.categoryLabel }}</span>
                  <span class="menu-value">
                    {{ item.name }}
                    <span v-if="item.containsPork" class="menu-pork">🐖 porc</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DashboardCard from "@/components/DashboardCard.vue"

export default {
  name: "NutritionistDashboard",
  components: { DashboardCard },
  data() {
    return {
      showAcceptance: false,
      showMenuPlanning: false,
      // Bloc « Acceptation » (§6.6.3, décision E) : moyenne par catégorie de menu sur le périmètre
      // nutritionniste, agrégée côté serveur. Aucune donnée fabriquée ni personnelle.
      acceptance: { displayed: false, categories: [] },
      acceptanceLoading: false,
      acceptanceError: null,
      // Bloc « Planification » (§6) : menus à venir sur le périmètre nutritionniste, regroupés par
      // date puis par cantine d'origine. Seules les recettes réellement importées sont restituées.
      menuPlanning: { days: [] },
      planningLoading: false,
      planningError: null,
    }
  },
  computed: {
    acceptanceBadge() {
      const best = this.acceptance.categories[0]
      return best ? this.formatRating(best.averageRating) : "—"
    },
    planningBadge() {
      const count = this.menuPlanning.days.length
      return count ? `${count} j` : "—"
    },
  },
  mounted() {
    this.fetchAcceptance()
    this.fetchMenuPlanning()
  },
  methods: {
    formatRating(rating) {
      const value = Number(rating || 0)
      return `${value.toLocaleString("fr-FR", { maximumFractionDigits: 1 })}/5`
    },
    formatFullDate(isoDate) {
      if (!isoDate) return ""
      const formatted = new Date(isoDate).toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long",
      })
      return formatted.charAt(0).toUpperCase() + formatted.slice(1)
    },
    async fetchAcceptance() {
      this.acceptanceLoading = true
      this.acceptanceError = null
      try {
        const response = await fetch("/api/v1/nutritionAcceptance/", { credentials: "same-origin" })
        if (response.ok) {
          const data = await response.json()
          this.acceptance = { displayed: !!data.displayed, categories: data.categories || [] }
        } else {
          this.acceptanceError = `Erreur ${response.status} lors du chargement de l'acceptation.`
        }
      } catch {
        this.acceptanceError = "Impossible de charger l'acceptation des menus."
      } finally {
        this.acceptanceLoading = false
      }
    },
    async fetchMenuPlanning() {
      this.planningLoading = true
      this.planningError = null
      try {
        const response = await fetch("/api/v1/nutritionMenuPlanning/", { credentials: "same-origin" })
        if (response.ok) {
          const data = await response.json()
          this.menuPlanning = { days: data.days || [] }
        } else {
          this.planningError = `Erreur ${response.status} lors du chargement de la planification.`
        }
      } catch {
        this.planningError = "Impossible de charger la planification des menus."
      } finally {
        this.planningLoading = false
      }
    },
    goToActionPage() {
      this.$router.push({ name: "CreateEatingExperience" })
    },
    goToActionPlan() {
      this.$router.push({ name: "ActionPlan" })
    },
    goToOnEatingIssuePage() {
      this.$router.push({ name: "EatingIssuePrediction" })
    },
  },
}
</script>

<style scoped>
.nutritionist-dashboard {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
  background-color: #f8f9fa;
  min-height: 100vh;
}

.dashboard-header {
  margin-bottom: 2rem;
  text-align: center;
}

.dashboard-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.dashboard-subtitle {
  font-size: 0.95rem;
  color: #6b7280;
  margin: 0;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.25rem;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  overflow-y: auto;
}

.modal-content {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.modal-large {
  max-width: 700px;
}

.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: #f3f4f6;
  border: none;
  cursor: pointer;
  font-size: 1.25rem;
  color: #6b7280;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 1.5rem 0;
}

.feedback-empty {
  font-size: 0.875rem;
  color: #6b7280;
  text-align: center;
  padding: 1.5rem 0;
}

/* Acceptation */
.acceptance-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.acceptance-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
}

.acceptance-category {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.category-name {
  font-weight: 600;
  color: #1a1a1a;
}

.acceptance-meta {
  font-size: 0.75rem;
  color: #6b7280;
}

.acceptance-score {
  font-weight: 700;
  font-size: 1.125rem;
}

.text-green {
  color: #22c55e;
}

.text-orange {
  color: #f97316;
}

/* Planning */
.detail-section {
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 1rem 0;
}

.planning-card {
  background: #f9fafb;
  padding: 1.25rem;
  border-radius: 12px;
  margin-bottom: 1rem;
}

.planning-date {
  font-size: 0.875rem;
  font-weight: 600;
  color: #2563eb;
  margin-bottom: 0.5rem;
}

.planning-canteen {
  margin-top: 0.75rem;
}

.planning-canteen:first-child {
  margin-top: 0;
}

.planning-canteen-name {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.35rem;
}

.menu-pork {
  font-size: 0.8125rem;
  color: #b45309;
  white-space: nowrap;
}

.menu-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.menu-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background: white;
  border-radius: 8px;
}

.menu-label {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

.menu-value {
  font-size: 0.875rem;
  color: #1a1a1a;
  font-weight: 600;
}

@media (max-width: 768px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }
}
</style>
